import cx from "classnames";
import { isEqual } from "lodash";
import { FC, JSX, useContext, useEffect, useState } from "react";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { BenchmarkingCounty } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingCounty";
import { BenchmarkingState } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingState";
import { buildStateCountyData } from "@app/overviews/network-benchmarking-dashboard/filters/shared/buildStateCountyData";
import { MembershipCountTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/MembershipCountTypeEnum";
import { useMedicaidPrimaryFilterOptionUpdates } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/hooks/useMedicaidPrimaryFilterOptionUpdates";
import { MedicaidFiltersContext } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidFiltersContext";
import { NetworkProgram } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/NetworkProgram";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { UserContext } from "@app/user/UserContext";

interface MedicaidAdvancedFiltersProps {
  onDirtyChange: (isDirty: boolean) => void;
}

const MedicaidAdvancedFilters: FC<MedicaidAdvancedFiltersProps> = (props: MedicaidAdvancedFiltersProps): JSX.Element => {
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const medicaidFiltersContext = useContext(MedicaidFiltersContext);
  const flyoutContext = useContext(FlyoutContext);
  const { clientId } = useContext(UserContext);

  const hasFilterData = !!benchmarkingFiltersContext.states.length;

  // every field below is staged locally and only written back to the shared contexts when Apply is
  // clicked — this keeps edits made in the flyout from leaking into the primary filters bar until then.
  // Line of Business is the exception: it writes straight through to benchmarkingFiltersContext so the
  // primary filters bar's dropdown stays in sync and the primary-filters API call fires immediately.
  const [stagedState, setStagedState] = useState<BenchmarkingState>(benchmarkingFiltersContext.selectedState);
  const [stagedProgramKey, setStagedProgramKey] = useState<string | null>(benchmarkingFiltersContext.programKey);
  const [stagedRawCounties, setStagedRawCounties] = useState<BenchmarkingCounty[]>(benchmarkingFiltersContext.rawCounties);
  const [stagedNestedCounties, setStagedNestedCounties] = useState<NestingParentDropdownItem[]>(benchmarkingFiltersContext.counties);
  const [stagedSelectedCounties, setStagedSelectedCounties] = useState<string[]>(benchmarkingFiltersContext.selectedCounties);
  const [stagedMembershipCount, setStagedMembershipCount] = useState<MembershipCountTypeEnum>(medicaidFiltersContext.selectedMembershipCount);

  const stagedPrograms: NetworkProgram[] = medicaidFiltersContext.allPrograms.filter(p => p.stateCode === stagedState?.stateAbbreviation);

  const stagedProgramDisabled = (): boolean => {
    return medicaidFiltersContext.primaryFiltersLoading
      || !hasFilterData
      || !stagedState
      || stagedPrograms.length === 0;
  }

  // re-fetches the program's county set whenever the staged state or program changes, so the Service
  // Area control only offers/selects counties that actually belong to the currently-staged program
  const { isLoading: stagedProgramCountiesLoading, primaryFilterUpdates: stagedProgramCountyUpdates } = useMedicaidPrimaryFilterOptionUpdates(
    clientId,
    stagedState?.stateId,
    stagedProgramKey,
    true
  );

  useEffect(() => {
    if (!stagedState || !stagedProgramCountyUpdates) return;

    const countyData = buildStateCountyData(stagedState, stagedProgramCountyUpdates.programCounties);
    const validCountyIds = new Set(countyData.rawCounties.map(x => x.countyId.toString()));

    setStagedRawCounties(countyData.rawCounties);
    setStagedNestedCounties(countyData.counties);
    // intersect rather than replace, so counties the user already picked stay selected if they're
    // still valid for the new program — only counties that no longer apply get dropped
    setStagedSelectedCounties(prev => prev.filter(x => validCountyIds.has(x)));
  }, [stagedProgramCountyUpdates]);

  const stagedServiceAreaDisabled = (): boolean => {
    return medicaidFiltersContext.primaryFiltersLoading
      || !hasFilterData
      || !stagedState
      || !stagedProgramKey
      || stagedProgramCountiesLoading;
  }


  const onApplyClick = (): void => {
    // setSelectedState re-derives counties for the new state on its own, so when the state actually
    // changed, apply the state and the staged county selection atomically — otherwise the
    // derive-counties effect would stomp the staged selection with "all counties in the new state"
    if (stagedState?.stateAbbreviation !== benchmarkingFiltersContext.selectedState?.stateAbbreviation) {
      benchmarkingFiltersContext.setSelectedStateAndCounties(stagedState, stagedSelectedCounties);
    } else {
      benchmarkingFiltersContext.setSelectedCounties(stagedSelectedCounties);
    }

    benchmarkingFiltersContext.setProgramKey(stagedProgramKey);
    medicaidFiltersContext.setSelectedMembershipCount(stagedMembershipCount);

    // applying commits the staged values, so the "unsaved changes" warning shouldn't fire for this close
    flyoutContext.clearWarnOnClose();
    flyoutContext.handleCloseFlyout();
  }

  const isDirty = (): boolean => {
    const countiesEqual = isEqual(
      [...stagedSelectedCounties].sort(),
      [...benchmarkingFiltersContext.selectedCounties].sort()
    );

    return stagedState?.stateAbbreviation !== benchmarkingFiltersContext.selectedState?.stateAbbreviation ||
      stagedProgramKey !== benchmarkingFiltersContext.programKey ||
      !countiesEqual ||
      stagedMembershipCount !== medicaidFiltersContext.selectedMembershipCount;
  }

  useEffect(() => {
    props.onDirtyChange(isDirty());
  });

  const onStagedStateChange = (stateAbbreviation: string): void => {
    const state: BenchmarkingState = benchmarkingFiltersContext.states.find(x => x.stateAbbreviation === stateAbbreviation);

    if (!!state) {
      setStagedState(state);

      const programsForState = medicaidFiltersContext.allPrograms.filter(p => p.stateCode === state.stateAbbreviation);
      const nextProgramKey = programsForState.length === 1 ? programsForState[0].programKey : null;
      setStagedProgramKey(nextProgramKey);

      // service area starts out as every county in the state; the query above narrows it down to the
      // staged program's counties (and intersects it with the selection above) once it resolves
      const countyData = buildStateCountyData(state, null);
      setStagedRawCounties(countyData.rawCounties);
      setStagedNestedCounties(countyData.counties);
      setStagedSelectedCounties(countyData.selectedCounties);
    }
  }

  const onStagedProgramChange = (programKey: string): void => {
    setStagedProgramKey(programKey);
  }

  const onCBSAMouseOver = (parentKey?: string): void => {
    if (!!parentKey) {
      const cbsa: NestingParentDropdownItem = stagedNestedCounties.find(x => x.key === parentKey);
      benchmarkingFiltersContext.setMousedOverCountyIds(cbsa?.children.map(x => x.key));
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  const onCountyCheckboxMouseOver = (dropdownItemKey?: string): void => {
    if (!!dropdownItemKey) {
      benchmarkingFiltersContext.setMousedOverCountyIds([dropdownItemKey]);
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  return (
    <div className={cx("qes-advanced-filters")}>
      <label className="copy-text-default d-block mb-3">
        Add filter criteria to refine your search results.
      </label>

      <QESSelect
        containerClassName="mb-3"
        disabled={benchmarkingFiltersContext.linesOfBusinessLoading || !benchmarkingFiltersContext.linesOfBusiness.length}
        testId="network-benchmarking-dashboard__advanced-filters--line-of-business"
        label="Line of Business"
        onChange={(e) => benchmarkingFiltersContext.setSelectedLineOfBusiness(parseInt(e.target.value))}
        value={benchmarkingFiltersContext.selectedLineOfBusiness || ""}
      >
        {Object.values(benchmarkingFiltersContext.linesOfBusiness).map((lineOfBusiness, index) => (
          <option
            key={index}
            value={lineOfBusiness}
          >
            {BenchmarkingLineOfBusinessEnum[lineOfBusiness]}
          </option>
        ))}
      </QESSelect>

      <QESSelect
        containerClassName="mb-3"
        disabled={medicaidFiltersContext.primaryFiltersLoading || !hasFilterData}
        testId="network-benchmarking-dashboard__advanced-filters--state"
        label="State"
        onChange={(e) => onStagedStateChange(e.target.value)}
        value={stagedState?.stateAbbreviation || ""}
      >
        {benchmarkingFiltersContext.states.map((state, index) => (
          <option
            key={index}
            value={state.stateAbbreviation}
          >
            {state.stateName}
          </option>
        ))}
      </QESSelect>

      <QESSelect
        containerClassName="mb-3"
        disabled={stagedProgramDisabled()}
        testId="network-benchmarking-dashboard__advanced-filters--program"
        label="Program"
        onChange={(e) => onStagedProgramChange(e.target.value)}
        value={stagedProgramKey || ""}
      >
        {stagedPrograms.map((program, index) => (
          <option
            key={index}
            value={program.programKey}
          >
            {program.programName}
          </option>
        ))}
      </QESSelect>

      <MultiselectNestedDropdown
        containerClassName="mb-3 d-block"
        customSelectInputText={(!!stagedRawCounties.length && stagedRawCounties.length === stagedSelectedCounties.length) ? "All" : null}
        disabled={stagedServiceAreaDisabled()}
        hasDuplicates={true}
        items={stagedNestedCounties}
        label="Service Area"
        onDropdownItemMouseOver={onCountyCheckboxMouseOver}
        onParentMouseOver={onCBSAMouseOver}
        onSelectionChanged={(selectedCounties: string[]) => { setStagedSelectedCounties(selectedCounties) }}
        selectedItems={stagedSelectedCounties}
        testId="network-benchmarking-dashboard__advanced-filters--county"
      />

      <hr />

      <QESSelect
        containerClassName="mb-3"
        disabled={medicaidFiltersContext.primaryFiltersLoading || !hasFilterData}
        testId="network-benchmarking-dashboard__advanced-filters--membership-count"
        label="Membership Count"
        onChange={(e) => setStagedMembershipCount(e.target.value as MembershipCountTypeEnum)}
        value={stagedMembershipCount || ""}
      >
        {Object.values(MembershipCountTypeEnum).map((membership, index) => (
          <option
            key={index}
            value={membership}
          >
            {`${membership}${membership !== MembershipCountTypeEnum.All ? "+" : ""}`}
          </option>
        ))}
      </QESSelect>

      <FlyoutFooter
        submitButton={
          <QESButton
            disabled={false}
            qesTestId="network-benchmarking-dashboard__advanced-filters--apply"
            qesType="primary"
            onClick={onApplyClick}
          >
            Apply
          </QESButton>
        }
      />
    </div>
  );
}

export default MedicaidAdvancedFilters;
